import React from "react";

import { Accordion, Chip, Text } from "@performyard/py-components";
import type { Level } from "@performyard/service-contracts";

const LEVEL_ACCORDION_TESTID = "competency-level-accordion";
// The shared Accordion renders its toggle button as `${data-testid}-header`.
export const LEVEL_ACCORDION_TOGGLE_TESTID = `${LEVEL_ACCORDION_TESTID}-header`;
export const LEVEL_ACCORDION_DIVIDER_TESTID = `${LEVEL_ACCORDION_TESTID}-divider`;

type LevelAccordionRowProps = {
	level: Level;
	isLowest: boolean;
	isHighest: boolean;
	isCurrent: boolean;
	isExpected: boolean;
	isExpanded: boolean;
	isLast: boolean;
	onToggle: () => void;
};

export const LevelAccordionRow = ({
	level,
	isLowest,
	isHighest,
	isCurrent,
	isExpected,
	isExpanded,
	isLast,
	onToggle,
}: LevelAccordionRowProps): React.ReactElement => {
	const suffix = isLowest ? " (Lowest)" : isHighest ? " (Highest)" : "";

	return (
		<>
			<Accordion
				variant="underline"
				size="small"
				isOpen={isExpanded}
				onToggle={onToggle}
				data-testid={LEVEL_ACCORDION_TESTID}
				aria-label={`${isExpanded ? "Collapse" : "Expand"} ${level.name}`}
				headerContent={
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<Text size="paragraph-3" weight="semi-bold" dangerouslySetClassName="min-w-0">
							<span className="py-line-clamp-1">{level.name}</span>
						</Text>
						<Text size="caption" weight="regular" color="neutral-5" dangerouslySetClassName="shrink-0">
							{`Level ${level.rank}${suffix}`}
						</Text>
						{isCurrent && <Chip label="Current" color="green" size={20} />}
						{isExpected && <Chip label="Expected" color="primary" size={20} />}
					</div>
				}
			>
				{level.description ? (
					<Text size="paragraph-3" weight="regular" dangerouslySetClassName="whitespace-pre-wrap">
						{level.description}
					</Text>
				) : (
					// Same copy and treatment the View Levels side panel used for this case.
					<Text size="paragraph-3" weight="regular" color="neutral-5">
						No description has been added for this level
					</Text>
				)}
			</Accordion>
			{/* Open content otherwise runs straight into the next header; the last row is bounded by
			    the super row's own bottom padding instead. */}
			{isExpanded && !isLast && (
				<div data-testid={LEVEL_ACCORDION_DIVIDER_TESTID} className="border-b border-solid border-neutral-2" />
			)}
		</>
	);
};
